import { useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Fade,
  Stack,
  useTheme
} from "@mui/material"
import {
  Close as CloseIcon,
  Extension as IntegrationIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon
} from "@mui/icons-material"
import { useForm, Controller } from "react-hook-form"
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useGetIntegrationQuery,
  IntegrationType,
  AllowedOrderType
} from "src/graphql/quark/quark.type"
import OutlinedInput from "src/shared/components/outlined-input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { INTEGRATION_TYPE_OPTIONS, ORDER_TYPE_OPTIONS, INTEGRATION_STATUS_OPTIONS } from "../../constants"
import { Conditional } from "src/shared/components"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { useTypedSelector } from "src/shared/store/store"
import SelectRegisterField from "src/shared/container/select-register-field"
import IntegrationModalSkeleton from "./IntegrationModalSkeleton"

type IntegrationFormData = {
  id?: string
  name: string
  type: IntegrationType
  status: string
  allowedOrderType: AllowedOrderType[]
  charges: number
  logo: string
  locationId: string
  registerId: string
}

const initialValues: IntegrationFormData = {
  id: undefined,
  name: "",
  type: IntegrationType.Fbr,
  status: "ACTIVE",
  allowedOrderType: [AllowedOrderType.Normal],
  charges: 0,
  logo: "",
  locationId: "",
  registerId: ""
}

const CreateUpdateIntegrationModal = () => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const integrationId = searchParams.get("id")
  const formMode = searchParams.get("mode")
  const isOpen = !!formMode
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)

  const { data, loading: isGetIntegrationLoading } = useGetIntegrationQuery({
    variables: { getIntegrationId: integrationId as string },
    skip: !integrationId
  })
  const integration = data?.getIntegration

  const {
    control,
    handleSubmit,
    reset,
    watch
    // formState: { isDirty }
  } = useForm<IntegrationFormData>({
    defaultValues: initialValues
  })

  const [createIntegration, { loading: createLoading }] = useCreateIntegrationMutation()
  const [updateIntegration, { loading: updateLoading }] = useUpdateIntegrationMutation()

  const watchedType = watch("type")
  const registerId = watch("registerId")
  const watchedStatus = watch("status")
  const watchedOrderType = watch("allowedOrderType")

  console.log("registerId", { registerId, integration })
  useEffect(() => {
    if (integration && formMode === "edit") {
      reset({
        name: integration.name || "",
        type: integration.type || IntegrationType.Fbr,
        status: integration.status || "ACTIVE",
        allowedOrderType: integration.allowedOrderType || [AllowedOrderType.Normal],
        charges: integration.charges || 0,
        // integrationId: integration.integrationId || "",
        logo: integration.logo || "",
        locationId: integration.locationId || "",
        registerId: integration.registerId || "",
        id: integration.id || undefined
      })
    } else if (formMode === "create") {
      reset(initialValues)
    }
  }, [integration, formMode, reset])

  const onSubmit = async (data: IntegrationFormData) => {
    try {
      const integrationInput = {
        name: data.name,
        type: data.type,
        status: data.status,
        allowedOrderType: data.allowedOrderType,
        charges: Number(data.charges ?? null),
        // integrationId: data.integrationId,
        logo: data.logo,
        locationId: currentWhLoc?.id as string,
        registerId: data?.registerId || (registerId as string)
      }

      if (formMode === "create") {
        const resp = await createIntegration({
          variables: { createIntegrationDto: integrationInput },
          refetchQueries: ["GetIntegrations"]
        })
        resp.data?.createIntegration.id
          ? toast.success("Integration created successfully")
          : toast.error("Failed to create integration")
      } else {
        const resp = await updateIntegration({
          variables: {
            updateIntegrationInput: {
              ...integrationInput,
              id: integration?.id as string
            }
          },
          refetchQueries: ["GetIntegrations"]
        })
        resp.data?.updateIntegration
          ? toast.success("Integration updated successfully")
          : toast.error("Failed to update integration")
      }
      handleClose()
    } catch (error) {
      console.error("Error saving integration:", error)
      toast.error("An error occurred while saving the integration")
    }
  }

  const handleClose = () => {
    reset(initialValues)
    setSearchParams({})
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case IntegrationType.Fbr:
        return <PaymentIcon />
      case IntegrationType.Srb:
        return <ShippingIcon />
      default:
        return <IntegrationIcon />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case IntegrationType.Fbr:
        return "primary"
      case IntegrationType.Srb:
        return "success"
      default:
        return "default"
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success"
      case "INACTIVE":
        return "warning"
      case "PENDING":
        return "info"
      case "ERROR":
        return "error"
      default:
        return "default"
    }
  }

  const getOrderTypeColor = (orderType: string) => {
    switch (orderType) {
      case AllowedOrderType.Return:
        return "primary"
      case AllowedOrderType.Normal:
        return "success"
      default:
        return "default"
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "600px"
        }
      }}
    >
      <DialogTitle
        sx={{
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: theme.palette.primary.contrastText,
          py: 3,
          px: 4,
          position: "relative"
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {formMode === "create" ? "Create New Integration" : "Edit Integration"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {formMode === "create" ? "Configure a new third-party integration" : "Update integration configuration"}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: theme.palette.primary.contrastText
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ background: theme.palette.background.default }}>
        <Conditional condition={!isGetIntegrationLoading} elseBlock={<IntegrationModalSkeleton />}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid2 container spacing={3} sx={{ py: 4 }}>
              {/* Basic Information Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Basic Information
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Integration name is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Integration Name"
                          placeholder="Enter integration name"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: "Integration type is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Integration Type"
                          options={INTEGRATION_TYPE_OPTIONS}
                          value={INTEGRATION_TYPE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: "Status is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Status"
                          options={INTEGRATION_STATUS_OPTIONS}
                          value={INTEGRATION_STATUS_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Configuration Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <IntegrationIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Integration Configuration
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="allowedOrderType"
                      control={control}
                      rules={{ required: "Order type is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.map((op: any) => op.value))
                          }}
                          label="Allowed Order Type"
                          options={ORDER_TYPE_OPTIONS}
                          value={ORDER_TYPE_OPTIONS.filter(
                            (option) => field.value?.length && field.value?.find((op) => op === option.value)
                          )}
                          multiple={true}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="charges"
                      control={control}
                      rules={{
                        required: "Charges is required",
                        min: { value: 0, message: "Charges must be positive" }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          type="number"
                          fullWidth
                          label="Charges ($)"
                          placeholder="Enter charges"
                          error={!!error}
                          required
                          helperText={error?.message}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="registerId"
                      control={control}
                      rules={{ required: "Register is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <SelectRegisterField
                          onChange={field.onChange}
                          value={field.value}
                          label="Register"
                          placeholder="Select register"
                          error={!!error}
                          required
                          helperText={error?.message}
                          locationId={currentWhLoc?.id as string}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="logo"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Logo URL"
                          placeholder="Enter logo URL"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Current Selection Display */}
              <Grid2 size={12}>
                <Fade in={true}>
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      "& .MuiAlert-message": { width: "100%" }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight="bold">
                        Current Configuration:
                      </Typography>
                      <Chip
                        icon={getTypeIcon(watchedType)}
                        label={INTEGRATION_TYPE_OPTIONS.find((opt) => opt.value === watchedType)?.label}
                        color={getTypeColor(watchedType) as any}
                        size="small"
                      />
                      <Chip
                        label={INTEGRATION_STATUS_OPTIONS.find((opt) => opt.value === watchedStatus)?.label}
                        color={getStatusColor(watchedStatus) as any}
                        size="small"
                      />
                      {watchedOrderType.map((orderType) => (
                        <Chip
                          label={ORDER_TYPE_OPTIONS.find((opt) => opt.value === orderType)?.label}
                          color={getOrderTypeColor(orderType) as any}
                          size="small"
                          key={orderType}
                        />
                      ))}
                    </Stack>
                  </Alert>
                </Fade>
              </Grid2>
            </Grid2>
          </form>
        </Conditional>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 3,
          background: theme.palette.background.default
        }}
      >
        <Button onClick={handleClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          size="large"
          disabled={createLoading || updateLoading || isGetIntegrationLoading}
          startIcon={createLoading || updateLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            minWidth: 180,
            background: theme.palette.primary.main,
            "&:hover": {
              background: theme.palette.primary.dark
            }
          }}
        >
          {createLoading || updateLoading
            ? "Saving..."
            : formMode === "create"
              ? "Create Integration"
              : "Update Integration"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateUpdateIntegrationModal
